import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  LinearProgress,
  Typography,
  Tooltip,
  CardActionArea, // Import Tooltip
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useParams } from "react-router-dom";
import { MusicVideo, PlayArrow as PlayArrowIcon } from "@mui/icons-material"; // Import PlayArrowIcon
import AlertPopup from "../../../../../../Components/Bits/AlertPopup";
import StickyAudioPlayer from "../../Admin/Evaluations/components/StickyAudioPlayer";
// Remove the direct import of the old AudioPlayer if it's no longer used in the list
// import AudioPlayer from "../../Admin/Evaluations/components/AudioPlayer";

const CallRecordingsNew = ({
  updateCallListeningTime,
  evaluation,
  getCallRecordings,
}) => {
  const { evalID } = useParams();
  const [localRecordings, setLocalRecordings] = useState([]); // For upload selection
  const [isUploading, setIsUploading] = useState(false);

  // --- State for Sticky Player ---
  const [currentPlayingId, setCurrentPlayingId] = useState(null);
  const [isStickyPlayerOpen, setIsStickyPlayerOpen] = useState(false);

  // --- Time Tracking Refs --- remains the same
  const lastReportedTimeRef = useRef({});
  const accumulatedTimeRef = useRef({});
  const [timeUpdateTrigger, setTimeUpdateTrigger] = useState(0);

  // --- Get Recordings Data ---
  const Recordings = getCallRecordings.data?.data?.data || []; // Ensure default empty array

  // --- Upload Mutation ---
  const postRecording = useMutation({
    mutationFn: (data) => {
      return axios.post(
        `${process.env.REACT_APP_API_URL}/QAInbound/admin/${evalID}/recording`,
        data
      );
    },
    onSuccess: () => {
      getCallRecordings.refetch();
      setLocalRecordings([]);
      setIsUploading(false);
    },
    onError: () => {
      setIsUploading(false);
    },
  });

  // --- Calculate Total Time and Update Parent --- remains the same
  useEffect(() => {
    const totalTime = Object.values(accumulatedTimeRef.current).reduce(
      (sum, time) => sum + (time || 0),
      0
    );
    // console.log("Updating parent with total time:", Math.round(totalTime));
    updateCallListeningTime(Math.round(totalTime));
  }, [timeUpdateTrigger, updateCallListeningTime]);

  // --- Time Tracking Callbacks (Now potentially called by Sticky Player) ---
  const handleTimeUpdate = useCallback(
    (audioElement, recordingId) => {
      if (!audioElement || !recordingId) {
        // console.warn("handleTimeUpdate called with missing data");
        return;
      }
      // Don't run if player is closing / id mismatch
      if (recordingId !== currentPlayingId) return;

      const currentTime = audioElement.currentTime;
      const lastTime = lastReportedTimeRef.current[recordingId] || 0;
      // Prevent large jumps if seeking or starting
      const timeDiff =
        currentTime > lastTime && currentTime - lastTime < 2
          ? currentTime - lastTime
          : 0;

      if (timeDiff > 0) {
        accumulatedTimeRef.current[recordingId] =
          (accumulatedTimeRef.current[recordingId] || 0) + timeDiff;
        // console.log(`Accumulated time for ${recordingId}: ${accumulatedTimeRef.current[recordingId]}`);
        setTimeUpdateTrigger((prev) => prev + 1); // Trigger parent update
      }
      lastReportedTimeRef.current[recordingId] = currentTime;
    },
    [currentPlayingId]
  ); // Add currentPlayingId dependency

  const handlePlay = useCallback((audioElement, recordingId) => {
    if (!audioElement || !recordingId) {
      console.warn("handlePlay called with missing data");
      return;
    }
    console.log(`handlePlay called for ${recordingId}`);
    // Reset last reported time when play starts
    lastReportedTimeRef.current[recordingId] = audioElement.currentTime;
    // No need to update accumulated time here
  }, []);

  const handlePauseOrEnd = useCallback(
    (audioElement, recordingId) => {
      if (!audioElement || !recordingId) {
        console.warn("handlePauseOrEnd called with missing data");
        return;
      }
      // Don't run if player is closing / id mismatch
      if (recordingId !== currentPlayingId) return;

      console.log(`handlePauseOrEnd called for ${recordingId}`);
      const currentTime = audioElement.currentTime;
      const lastTime = lastReportedTimeRef.current[recordingId] || 0;
      // Allow slightly larger jumps on pause/end in case of lag
      const timeDiff =
        currentTime > lastTime && currentTime - lastTime < 5
          ? currentTime - lastTime
          : 0;

      if (timeDiff > 0) {
        accumulatedTimeRef.current[recordingId] =
          (accumulatedTimeRef.current[recordingId] || 0) + timeDiff;
        // console.log(`Accumulated time for ${recordingId} (on pause/end): ${accumulatedTimeRef.current[recordingId]}`);
        setTimeUpdateTrigger((prev) => prev + 1); // Trigger parent update
      }
      // Update last reported time even if diff is 0
      lastReportedTimeRef.current[recordingId] = currentTime;
    },
    [currentPlayingId]
  ); // Add currentPlayingId dependency

  // --- Sticky Player Controls ---
  const handlePlayRequest = useCallback((recordingId) => {
    console.log("Play requested for:", recordingId);
    // If the same track is requested while open, maybe toggle play/pause?

    setCurrentPlayingId(recordingId);
    setIsStickyPlayerOpen(true);
  }, []);

  const handleCloseStickyPlayer = useCallback(() => {
    console.log("Closing sticky player");
    setIsStickyPlayerOpen(false);
    // Optionally pause the audio by resetting the ID, which triggers useEffect in StickyPlayer
    // Call the pause/end handler manually to capture final time segment
    const activeAudio = document.querySelector(
      'audio[src*="' + currentPlayingId + '"]'
    ); // Find the active audio element
    if (activeAudio && currentPlayingId) {
      handlePauseOrEnd(activeAudio, currentPlayingId);
    }
    setCurrentPlayingId(null);
  }, [currentPlayingId, handlePauseOrEnd]); // Add dependencies

  // --- Upload Handlers ---
  const handleFileChange = (event) => {
    const newRecordings = Array.from(event.target.files).filter((file) =>
      file.type.startsWith("audio/")
    );
    setLocalRecordings(newRecordings);
  };

  const handleSave = () => {
    if (localRecordings.length === 0) return;
    setIsUploading(true);
    const formData = new FormData();
    localRecordings.forEach((file) => {
      formData.append("file", file);
    });
    postRecording.mutate(formData);
  };

  // --- Loading/Error States ---
  if (getCallRecordings.isLoading) {
    return <LinearProgress />;
  }
  if (getCallRecordings.isError) {
    return <Alert severity="error">Error loading recordings.</Alert>;
  }

  return (
    <Card
      sx={{
        my: 2,
        pb: isStickyPlayerOpen ? "100px" : 0,
        borderStyle: "dashed",
        borderColor: "primary.main",
      }}
    >
      {" "}
      {/* Add paddingBottom when sticky player is open */}
      {/* --- Upload Section --- */}
      {(evaluation?.status === "New" || evaluation?.status === "Busy") && (
        <Box>
          <input
            accept=".mp3,.wav,.ogg,.m4a,.aac,audio/*"
            id="Call-Recordings-Input"
            type="file"
            multiple
            onChange={handleFileChange}
            style={{ display: "none" }}
            onClick={(event) => {
              event.target.value = null;
            }}
          />
          {localRecordings.length === 0 ? (
            <label htmlFor="Call-Recordings-Input">
              <Button
                variant="outlined"
                sx={{
                  width: "100%",
                }}
                startIcon={<MusicVideo />}
                component="span"
                disabled={isUploading || postRecording.isLoading}
              >
                Upload New Call Recording(s)
              </Button>
            </label>
          ) : (
            <Box>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" fontWeight="bold">
                  Selected:
                </Typography>
                {localRecordings.map((file) => (
                  <Typography variant="caption" key={file.name}>
                    {file.name}
                  </Typography>
                ))}
              </Stack>
              <Button
                sx={{ px: 5, py: 1 }}
                size="medium"
                color="secondary"
                variant="contained"
                fullWidth
                onClick={handleSave}
                disabled={isUploading || postRecording.isLoading}
              >
                {isUploading || postRecording.isLoading ? (
                  <LinearProgress color="inherit" sx={{ width: "80px" }} />
                ) : (
                  "Upload Selected"
                )}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {/* --- Recordings List --- */}
      {Recordings?.length > 0 && (
        <Card sx={{ p: 1 }}>
          <>
            <Typography variant="h6" gutterBottom>
              Recordings
            </Typography>
            {(evaluation?.status === "New" ||
              evaluation?.status === "Busy") && (
              <Alert sx={{ mb: 2 }} severity="warning">
                Remember to SAVE progress after listening to avoid losing
                tracked time. Playing audio will open the player at the bottom.
              </Alert>
            )}
            <Stack spacing={1.5}>
              {" "}
              {/* Adjusted spacing slightly */}
              {Recordings.map((recording) => (
                <CardActionArea
                  key={recording.id}
                  onClick={() => handlePlayRequest(recording.id)}
                >
                  <Box
                    key={recording.id}
                    display="flex"
                    flexDirection="row" // Keep as row
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      gap: 2,
                      boxShadow: 1,
                      borderRadius: 1,
                      padding: 1,
                      backgroundColor:
                        currentPlayingId === recording.id
                          ? "action.hover"
                          : "transparent", // Highlight playing track
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    {/* Recording Name */}
                    <Typography
                      variant="body2"
                      sx={{
                        flexGrow: 1,
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {recording.name ||
                        `Recording ${recording.id.substring(0, 8)}`}
                    </Typography>

                    {/* Play Button */}
                    <Tooltip title="Play Recording">
                      <Button
                        onClick={() => handlePlayRequest(recording.id)}
                        size="small"
                        color="primary"
                        endIcon={<PlayArrowIcon />}
                        disabled={
                          currentPlayingId === recording.id &&
                          isStickyPlayerOpen
                        } // Disable if currently playing in sticky player
                      >
                        {currentPlayingId === recording.id ? (
                          <LinearProgress
                            color="inherit"
                            sx={{ width: "20px" }}
                          />
                        ) : (
                          "Play Recording"
                        )}
                      </Button>
                    </Tooltip>

                    {/* --- REMOVED Individual AudioPlayer from list ---
                  <AudioPlayer
                    src={`${process.env.REACT_APP_API_URL}/QAInbound/admin/${recording.id}/recording/download`}
                    recordingId={recording.id}
                    onTimeUpdate={handleTimeUpdate} // Pass the same callbacks
                    onPlayCallback={handlePlay}     // Pass the same callbacks
                    onPauseCallback={handlePauseOrEnd} // Pass the same callbacks
                    onEndedCallback={handlePauseOrEnd} // Pass the same callbacks
                    disabled={false} // Control disabled state if needed
                    sx={{ flexGrow: 1, minWidth: 250 }}
                  />
                   */}
                  </Box>
                </CardActionArea>
              ))}
            </Stack>
          </>
        </Card>
      )}
      {/* --- Sticky Player --- */}
      {/* Render based on isStickyPlayerOpen AND currentPlayingId */}
      {isStickyPlayerOpen && currentPlayingId && (
        <StickyAudioPlayer
          allRecordings={Recordings}
          currentPlayingId={currentPlayingId}
          onClose={handleCloseStickyPlayer}
          onPlayRequest={handlePlayRequest} // Allows switching tracks from the sticky player list
          // Pass down the time tracking handlers
          onTimeUpdate={handleTimeUpdate}
          onPlayCallback={handlePlay}
          onPauseCallback={handlePauseOrEnd}
          onEndedCallback={handlePauseOrEnd}
        />
      )}
      {/* --- Alert Popups --- */}
      <AlertPopup
        open={postRecording.isSuccess}
        message="Recording added Successfully"
        severity="success"
        onClose={() => postRecording.reset()}
      />
      <AlertPopup
        open={postRecording.isError}
        message={
          postRecording.error?.response?.data?.message ||
          "Error adding recording"
        }
        severity="error"
        onClose={() => postRecording.reset()}
      />
    </Card>
  );
};

export default CallRecordingsNew;
